/**
 * Harvest Types - Temporary Staging for Book Content
 *
 * A HarvestBucket is the intermediate staging area between:
 * - Archive semantic search results
 * - Curated passages in a BookProject
 *
 * This abstraction enables:
 * - Review before commit (don't pollute BookProject with noise)
 * - Batch operations (approve/reject multiple at once)
 * - AUI-driven harvesting with user oversight
 * - Deduplication and quality filtering
 */
// ═══════════════════════════════════════════════════════════════════
// FACTORY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════
/**
 * Create a new harvest bucket
 */
export function createHarvestBucket(bookUri, queries, options) {
    const id = `harvest-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const now = Date.now();
    return {
        id,
        uri: `harvest://${id}`,
        type: 'harvest-bucket',
        name: `Harvest ${new Date().toISOString().slice(0, 10)}`,
        createdAt: now,
        updatedAt: now,
        tags: [],
        bookUri,
        threadUri: options?.threadUri,
        status: 'collecting',
        queries,
        config: {
            queriesPerThread: 10,
            minWordCount: 50,
            maxWordCount: 500,
            minSimilarity: 0.65,
            dedupeByContent: true,
            dedupeThreshold: 0.9,
            ...options?.config,
        },
        initiatedBy: options?.initiatedBy || 'user',
        candidates: [],
        approved: [],
        gems: [],
        rejected: [],
        duplicateIds: [],
        stats: {
            totalCandidates: 0,
            reviewed: 0,
            approved: 0,
            gems: 0,
            rejected: 0,
            duplicates: 0,
            avgSimilarity: 0,
            approvedWordCount: 0,
        },
        startedAt: now,
    };
}
/**
 * Create a new narrative arc
 */
export function createNarrativeArc(bookUri, thesis, options) {
    const id = `arc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const now = Date.now();
    return {
        id,
        uri: `arc://${id}`,
        type: 'narrative-arc',
        name: 'Narrative Arc',
        createdAt: now,
        updatedAt: now,
        tags: [],
        bookUri,
        arcType: options?.arcType || 'linear',
        thesis,
        themes: [],
        chapters: [],
        openingStrength: 0,
        resolutionQuality: 0,
        proposedBy: options?.proposedBy || 'aui',
        evaluation: {
            status: 'pending',
        },
    };
}
/**
 * Create a passage link
 */
export function createPassageLink(passageId, chapterId, position, options) {
    return {
        id: `link-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        passageId,
        chapterId,
        sectionId: options?.sectionId,
        position,
        usageType: options?.usageType || 'verbatim',
        createdAt: Date.now(),
        createdBy: options?.createdBy || 'user',
    };
}
// ═══════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════
/**
 * Check if a harvest bucket is in a terminal state
 */
export function isHarvestTerminal(bucket) {
    return bucket.status === 'committed' || bucket.status === 'discarded';
}
/**
 * Check if a harvest bucket is ready to commit
 */
export function isHarvestReady(bucket) {
    return bucket.status === 'staged' && bucket.approved.length > 0;
}
/**
 * Calculate harvest progress (0-100)
 */
export function getHarvestProgress(bucket) {
    const total = bucket.candidates.length;
    if (total === 0)
        return 0;
    return Math.round((bucket.stats.reviewed / total) * 100);
}
/**
 * Get all approved passages (approved + gems)
 */
export function getAllApproved(bucket) {
    return [...bucket.approved, ...bucket.gems];
}
/**
 * Check if an arc is approved
 */
export function isArcApproved(arc) {
    return arc.evaluation.status === 'approved';
}
/**
 * Get unlinked passages (passages not used in any chapter)
 */
export function getOrphanedPassages(passageIds, links) {
    const linkedIds = new Set(links.map((l) => l.passageId));
    return passageIds.filter((id) => !linkedIds.has(id));
}
/**
 * Get passage usage count across chapters
 */
export function getPassageUsageCount(passageId, links) {
    return links.filter((l) => l.passageId === passageId).length;
}
//# sourceMappingURL=harvest.js.map